/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { Loader2, Play } from 'lucide-react'
import Image from 'next/image'
import React, { HTMLAttributes, useEffect, useId, useState } from 'react'

import { VimeoModalPlayer } from '@/components/e-commerce/kits/kit-gallery-carousel/vimeo/VimeoModalPlayer'
import { RichTextRenderer } from '@/components/storyblok/components/ui/RichtextRenderer'
import { CaseStudyEntryStoryblok } from '@/lib/storyblok/types/generated-types'
import { getAssetImageDimensions } from '@/lib/storyblok/utils/image-asset'
import { fetchVimeoMetadata, VimeoMetadata } from '@/lib/vimeo/metadata'
import { cn } from '@/utils/css'

/**
 * Individual card that belongs inside the {@link CaseStudyList} component. The cover image can either be an image or
 * video. If it's a video, it'll be a clickable thumbnail with a 'play' icon overlaying that opens the video in a modal.
 */
export const CaseStudyCard: React.FC<HTMLAttributes<HTMLDivElement> & CaseStudyEntryStoryblok> = ({
  cover_image,
  company_logo,
  text,
  ...props
}) => {
  const patternId = useId()
  const [playerOpen, setPlayerOpen] = useState(false)
  const [vimeoMetadata, setVimeoMetadata] = useState<VimeoMetadata | null>(null)

  const isVideo = cover_image.filename.includes('vimeo.com')
  const videoId = isVideo ? cover_image.filename.split('/').pop()?.split('?')[0] : ''

  useEffect(() => {
    if (isVideo && videoId && !vimeoMetadata) {
      fetchVimeoMetadata(videoId).then((data) => setVimeoMetadata(data))
    }
  }, [isVideo, videoId, vimeoMetadata])

  // Determine the URL for the SVG pattern image (either original cover or Vimeo thumbnail)
  const svgPatternImageUrl = isVideo && vimeoMetadata ? vimeoMetadata.thumbnail_large : cover_image.filename

  return (
    <div className={'w-full flex flex-col'} {...props}>
      <div className={'relative'}>
        {playerOpen && isVideo && videoId && vimeoMetadata && (
          <VimeoModalPlayer videoId={videoId} onClose={() => setPlayerOpen(false)} vimeoMetadata={vimeoMetadata} />
        )}
        <svg
          width={'100%'}
          height={'100%'}
          viewBox={'0 0 366 172'}
          fill={'none'}
          xmlns={'http://www.w3.org/2000/svg'}
          className={cn('w-full h-full drop-shadow-md', isVideo && 'cursor-pointer')}
          onClick={isVideo ? () => setPlayerOpen(true) : undefined}
        >
          <defs>
            <filter id='blurredImage'>
              <feGaussianBlur stdDeviation='10' />
            </filter>
            <pattern id={patternId} patternUnits={'userSpaceOnUse'} width={'100%'} height={'100%'}>
              {svgPatternImageUrl && (
                <>
                  <image
                    href={svgPatternImageUrl}
                    width={'100%'}
                    height={'100%'}
                    preserveAspectRatio={'xMidYMid slice'}
                    filter='url(#blurredImage)'
                  />
                  <image
                    href={svgPatternImageUrl}
                    width={'100%'}
                    height={'100%'}
                    preserveAspectRatio={'xMidYMid meet'}
                  />
                </>
              )}
              {!svgPatternImageUrl && <rect width={'100%'} height={'100%'} fill={'#E0E0E0'} />}
            </pattern>
          </defs>
          <path
            fillRule={'evenodd'}
            clipRule={'evenodd'}
            d={
              'M366 12C366 5.37258 360.627 0 354 0H12C5.37258 0 0 5.37258 0 12V160C0 166.627 5.37258 172 12 172H294C300.627 172 306 166.627 306 160V124C306 117.373 311.373 112 318 112H354C360.627 112 366 106.627 366 100V12Z'
            }
            fill={`url(#${patternId})`}
            strokeWidth={2}
            stroke={'white'}
          />
        </svg>
        {isVideo && !vimeoMetadata && (
          <div className={'absolute inset-0 flex items-center justify-center'}>
            <Loader2 className={'animate-spin'} size={24} />
          </div>
        )}
        {isVideo && vimeoMetadata && (
          <Play
            className={cn(
              'z-10 cursor-pointer absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
              'p-3 w-12 h-12 text-white bg-black/35 rounded-full border-2 border-white',
            )}
            onClick={() => setPlayerOpen(true)}
          />
        )}
        <div className={'absolute inset-0 pointer-events-none'}>
          <div className={'absolute aspect-square w-[11.5%]'} style={{ bottom: '5%', right: '2.5%' }}>
            <Image
              src={company_logo.filename}
              alt={company_logo.alt ?? ''}
              className={'w-full h-full object-contain'}
              {...getAssetImageDimensions(company_logo)}
            />
          </div>
        </div>
      </div>
      <div className={'mt-2'}>
        <RichTextRenderer document={text} />
      </div>
    </div>
  )
}
